// Performance Optimization Through Intelligent Caching
// Implements multi-level caching with invalidation strategies

import type { OrderBookEntry, Trade } from '../types';
import type { AdaptiveParameters } from './adaptiveParameters';

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  hash: string;
  hitCount: number;
  computeTime: number;
}

export interface CacheStats {
  hits: number;
  misses: number;
  evictions: number;
  totalComputeTime: number;
  averageComputeTime: number;
  hitRate: number;
  memoryUsage: number;
}

export class AnalyticsCache {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private stats: CacheStats;
  private maxSize: number;
  private defaultTtl: number;

  constructor(maxSize: number = 1000, defaultTtl: number = 30000) {
    this.maxSize = maxSize;
    this.defaultTtl = defaultTtl;
    this.stats = {
      hits: 0,
      misses: 0,
      evictions: 0,
      totalComputeTime: 0,
      averageComputeTime: 0,
      hitRate: 0,
      memoryUsage: 0
    };
  }

  // Generate cache key from input data
  private generateKey(prefix: string, inputs: any[]): string {
    const inputHash = this.hashInputs(inputs);
    return `${prefix}:${inputHash}`;
  }

  // Simple hash function for input data
  private hashInputs(inputs: any[]): string {
    const str = JSON.stringify(inputs, (key, value) => {
      if (typeof value === 'number') {
        return Math.round(value * 1000000) / 1000000; // Round to 6 decimal places
      }
      return value;
    });

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
  }

  // Get cached result or compute if needed
  get<T>(
    key: string,
    computeFn: () => T,
    ttl?: number,
    skipCache: boolean = false
  ): T {
    if (skipCache) {
      const startTime = performance.now();
      const result = computeFn();
      const computeTime = performance.now() - startTime;
      this.stats.totalComputeTime += computeTime;
      return result;
    }

    const cached = this.cache.get(key);
    const now = Date.now();
    const effectiveTtl = ttl || this.defaultTtl;

    // Check if cached entry is valid
    if (cached && (now - cached.timestamp) < effectiveTtl) {
      cached.hitCount++;
      this.stats.hits++;
      this.updateStats();
      return cached.data;
    }

    // Cache miss - compute new value
    this.stats.misses++;
    const startTime = performance.now();
    const result = computeFn();
    const computeTime = performance.now() - startTime;

    // Store in cache
    this.set(key, result, computeTime);
    this.updateStats();

    return result;
  }

  // Set cache entry
  private set<T>(key: string, data: T, computeTime: number): void {
    // Evict oldest entries if cache is full
    if (this.cache.size >= this.maxSize) {
      this.evictOldest();
    }

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      hash: this.hashInputs([data]),
      hitCount: 0,
      computeTime
    };

    this.cache.set(key, entry);
    this.stats.totalComputeTime += computeTime;
  }

  // Evict oldest entries based on LRU + hit count
  private evictOldest(): void {
    let oldestKey = '';
    let oldestScore = Infinity;

    // Score = (age * 1000) / (hitCount + 1) - lower score = better candidate for eviction
    this.cache.forEach((entry, key) => {
      const age = Date.now() - entry.timestamp;
      const score = age / (entry.hitCount + 1);

      if (score < oldestScore) {
        oldestScore = score;
        oldestKey = key;
      }
    });

    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.stats.evictions++;
    }
  }

  // Update cache statistics
  private updateStats(): void {
    const total = this.stats.hits + this.stats.misses;
    this.stats.hitRate = total > 0 ? this.stats.hits / total : 0;
    this.stats.averageComputeTime = this.stats.misses > 0 ?
      this.stats.totalComputeTime / this.stats.misses : 0;

    // Estimate memory usage (rough calculation)
    this.stats.memoryUsage = this.cache.size * 1024; // Assume 1KB per entry
  }

  // Invalidate cache entries by pattern
  invalidatePattern(pattern: string): number {
    let count = 0;
    this.cache.forEach((_entry, key) => {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        count++;
      }
    });
    return count;
  }

  // Clear all cache
  clear(): void {
    this.cache.clear();
    this.stats = {
      hits: 0,
      misses: 0,
      evictions: 0,
      totalComputeTime: 0,
      averageComputeTime: 0,
      hitRate: 0,
      memoryUsage: 0
    };
  }

  // Get cache statistics
  getStats(): CacheStats {
    this.updateStats();
    return { ...this.stats };
  }

  // Adjust cache settings based on adaptive parameters
  updateSettings(params: AdaptiveParameters): void {
    this.defaultTtl = params.cacheTimeout;

    // Adjust cache size based on activity level
    if (params.maxHistorySize !== this.maxSize) {
      this.maxSize = Math.max(100, Math.min(5000, params.maxHistorySize));

      // Trim cache if new size is smaller
      while (this.cache.size > this.maxSize) {
        this.evictOldest();
      }
    }
  }
}

// Specialized cache for different analytics types
export class AnalyticsCacheManager {
  private depthCache: AnalyticsCache;
  private volatilityCache: AnalyticsCache;
  private fillProbCache: AnalyticsCache;
  private generalCache: AnalyticsCache;

  constructor() {
    this.depthCache = new AnalyticsCache(500, 15000); // 15s TTL for depth analysis
    this.volatilityCache = new AnalyticsCache(200, 60000); // 1m TTL for volatility
    this.fillProbCache = new AnalyticsCache(1000, 10000); // 10s TTL for fill probability
    this.generalCache = new AnalyticsCache(300, 30000); // 30s TTL for general analytics
  }

  // Cached depth imbalance calculation
  getDepthImbalance(
    orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] },
    topN: number,
    computeFn: () => any,
    params: AdaptiveParameters
  ): any {
    const key = this.generateKey('depth', [orderBook, topN]);
    return this.depthCache.get(
      key,
      computeFn,
      params.cacheTimeout * 0.5 // Shorter TTL for depth data
    );
  }

  // Cached volatility calculation
  getVolatilityMetrics(
    priceHistory: Array<{ time: string; price: number }>,
    currentSpread: number,
    lookbackPeriods: number,
    computeFn: () => any,
    params: AdaptiveParameters
  ): any {
    const key = this.generateKey('volatility', [priceHistory.slice(-lookbackPeriods), currentSpread]);
    return this.volatilityCache.get(
      key,
      computeFn,
      params.cacheTimeout * 2 // Longer TTL for volatility
    );
  }

  // Cached fill probability calculation
  getFillProbability(
    orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] },
    side: string,
    size: number,
    price: number,
    recentTrades: Trade[],
    computeFn: () => any,
    params: AdaptiveParameters
  ): any {
    const key = this.generateKey('fillprob', [orderBook, side, size, price, recentTrades.slice(-10)]);
    return this.fillProbCache.get(
      key,
      computeFn,
      params.cacheTimeout * 0.3 // Very short TTL for fill probability
    );
  }

  // Cached general analytics
  getGeneralAnalytics(
    inputs: any[],
    computeFn: () => any,
    params: AdaptiveParameters,
    cacheKey: string = 'general'
  ): any {
    const key = this.generateKey(cacheKey, inputs);
    return this.generalCache.get(key, computeFn, params.cacheTimeout);
  }

  // Generate cache key
  private generateKey(prefix: string, inputs: any[]): string {
    // For orderbook, only use top levels and sizes to reduce cache key size
    const processedInputs = inputs.map(input => {
      if (input && typeof input === 'object' && input.bids && input.asks) {
        return {
          bids: input.bids.slice(0, 5).map((b: OrderBookEntry) => ({ price: b.price, size: b.size })),
          asks: input.asks.slice(0, 5).map((a: OrderBookEntry) => ({ price: a.price, size: a.size }))
        };
      }
      if (Array.isArray(input) && input.length > 20) {
        return input.slice(-20); // Only use recent data for cache key
      }
      return input;
    });

    const inputHash = this.hashInputs(processedInputs);
    return `${prefix}:${inputHash}`;
  }

  private hashInputs(inputs: any[]): string {
    const str = JSON.stringify(inputs, (key, value) => {
      if (typeof value === 'number') {
        return Math.round(value * 100000) / 100000; // Round to 5 decimal places
      }
      return value;
    });

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }

  // Update all cache settings
  updateSettings(params: AdaptiveParameters): void {
    this.depthCache.updateSettings(params);
    this.volatilityCache.updateSettings(params);
    this.fillProbCache.updateSettings(params);
    this.generalCache.updateSettings(params);
  }

  // Invalidate caches when market conditions change significantly
  invalidateOnMarketChange(changeType: 'spread' | 'depth' | 'volatility' | 'all'): void {
    switch (changeType) {
      case 'spread':
        this.depthCache.invalidatePattern('depth');
        this.fillProbCache.invalidatePattern('fillprob');
        break;
      case 'depth':
        this.depthCache.clear();
        this.fillProbCache.clear();
        break;
      case 'volatility':
        this.volatilityCache.clear();
        this.generalCache.invalidatePattern('volatility');
        break;
      case 'all':
        this.clearAll();
        break;
    }
  }

  // Get combined statistics
  getAllStats(): { [key: string]: CacheStats } {
    return {
      depth: this.depthCache.getStats(),
      volatility: this.volatilityCache.getStats(),
      fillProbability: this.fillProbCache.getStats(),
      general: this.generalCache.getStats()
    };
  }

  // Clear all caches
  clearAll(): void {
    this.depthCache.clear();
    this.volatilityCache.clear();
    this.fillProbCache.clear();
    this.generalCache.clear();
  }

  // Warm up cache with common calculations
  warmUp(
    orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] },
    recentTrades: Trade[],
    priceHistory: Array<{ time: string; price: number }>,
    params: AdaptiveParameters
  ): void {
    // Pre-calculate common analytics to populate cache
    const commonSizes = [100, 500, 1000, 2000, 5000];
    const bestBid = orderBook.bids[0]?.price;
    const bestAsk = orderBook.asks[0]?.price;

    if (bestBid && bestAsk) {
      // Warm up fill probability cache
      for (const size of commonSizes) {
        const warmUpFn = () => ({ probability: 0.5, queuePosition: 1, estimatedWaitTime: 30 });
        this.getFillProbability(orderBook, 'buy', size, bestAsk, recentTrades, warmUpFn, params);
        this.getFillProbability(orderBook, 'sell', size, bestBid, recentTrades, warmUpFn, params);
      }
    }

    // Warm up depth cache
    const depthWarmUp = () => ({ imbalance: 0, bidVolume: 0, askVolume: 0 });
    this.getDepthImbalance(orderBook, 5, depthWarmUp, params);
    this.getDepthImbalance(orderBook, 10, depthWarmUp, params);

    // console.log('🔥 Analytics cache warmed up');
  }
}

// Singleton instance
export const analyticsCacheManager = new AnalyticsCacheManager();